import { signal } from '@preact/signals';
import { ApiError, api } from '../api';
import { reconectar } from '../session';
import { modoOffline } from '../state';
import { filaGravar, filaListar, filaRemover, type ItemFila } from './armazem';
import { desfechoDoEnvio } from './regras';

/** Fila de respostas do aparelho e envio para a API. Tudo que o pesquisador salva vai primeiro para o aparelho; o envio é um passo à parte. */

export const fila = signal<ItemFila[]>([]);
export const online = signal(typeof navigator === 'undefined' ? true : navigator.onLine);
export const sincronizando = signal(false);
/** Última vez que um envio terminou (a tela usa para recarregar o progresso). */
export const ultimoEnvio = signal(0);
/** Mensagem do último problema que impediu o envio (rede, limite, sessão), para a pessoa saber por que não foi. */
export const avisoEnvio = signal('');

let usuarioAtual = '';
let enviando = false;
let reagendar: ReturnType<typeof setTimeout> | null = null;

export const pendentes = () => fila.value.filter((i) => i.estado === 'pendente');
export const rejeitadas = () => fila.value.filter((i) => i.estado === 'rejeitada');

export async function carregarFila(usuarioId: string) {
  usuarioAtual = usuarioId;
  fila.value = await filaListar(usuarioId);
}

/** Guarda a resposta no aparelho (é isto que garante que nada se perde) e tenta enviar em seguida. */
export async function guardarResposta(item: ItemFila) {
  await filaGravar(item);
  fila.value = [...fila.value, item];
  void enviarPendentes();
}

async function atualizar(item: ItemFila) {
  await filaGravar(item);
  fila.value = fila.value.map((i) => (i.idLocal === item.idLocal ? item : i));
}

export async function descartar(idLocal: string) {
  await filaRemover(idLocal);
  fila.value = fila.value.filter((i) => i.idLocal !== idLocal);
}

/** A API recusou e a pessoa quer tentar de novo (por exemplo, depois que o gestor atribuiu o município). */
export async function tentarDeNovo(idLocal: string) {
  const i = fila.value.find((x) => x.idLocal === idLocal);
  if (!i) return;
  await atualizar({ ...i, estado: 'pendente', erro: undefined });
  void enviarPendentes();
}

/**
 * Envia as pendentes, da mais antiga para a mais nova, uma de cada vez.
 * Falta de rede, limite de requisições e erro do servidor: para e tenta de novo depois, sem perder nada.
 * Recusa definitiva da API: a resposta vira "rejeitada" com o motivo, para a pessoa ver (não some em silêncio).
 */
export async function enviarPendentes() {
  if (enviando || !navigator.onLine) return;
  // App aberto sem internet: ainda não há sessão de verdade. Renova antes de enviar; se não der, tenta depois.
  if (modoOffline.value) {
    enviando = true;
    const ok = await reconectar().catch(() => false);
    enviando = false;
    if (!ok) return;
  }
  enviando = true;
  sincronizando.value = true;
  avisoEnvio.value = '';
  let enviou = false;
  try {
    for (const item of pendentes()) {
      if (item.usuarioId !== usuarioAtual) continue;
      try {
        await api(`/pesquisas/${item.pesquisaId}/respostas`, { method: 'POST', body: item.payload });
        await descartar(item.idLocal);
        enviou = true;
      } catch (e) {
        const status = e instanceof ApiError ? e.status : 0;
        const d = desfechoDoEnvio(status);
        if (d === 'rejeitar') {
          await atualizar({ ...item, estado: 'rejeitada', erro: (e as Error).message, tentativas: item.tentativas + 1 });
          continue;
        }
        await atualizar({ ...item, tentativas: item.tentativas + 1 });
        avisoEnvio.value = d === 'sessao' ? 'Sua sessão acabou: entre de novo para enviar as respostas guardadas.' : status === 429 ? 'Muitos envios seguidos: tentando de novo em instantes.' : 'Sem conexão com o servidor: as respostas continuam guardadas e serão enviadas quando a internet voltar.';
        if (d === 'tentar_depois') agendar(status === 429 ? 65_000 : 30_000);
        break;
      }
    }
  } finally {
    enviando = false;
    sincronizando.value = false;
    if (enviou) ultimoEnvio.value = Date.now();
  }
}

function agendar(ms: number) {
  if (reagendar) clearTimeout(reagendar);
  reagendar = setTimeout(() => {
    reagendar = null;
    void enviarPendentes();
  }, ms);
}

/** Liga os gatilhos de envio: voltou a internet, o app voltou ao primeiro plano, ou passou um tempo com respostas guardadas. */
export function iniciarSincronizacao(): () => void {
  const aoVoltar = () => {
    online.value = true;
    void enviarPendentes();
  };
  const aoCair = () => {
    online.value = false;
  };
  const aoMostrar = () => {
    if (document.visibilityState === 'visible') void enviarPendentes();
  };
  window.addEventListener('online', aoVoltar);
  window.addEventListener('offline', aoCair);
  document.addEventListener('visibilitychange', aoMostrar);
  const periodico = setInterval(() => {
    if (pendentes().length > 0) void enviarPendentes();
  }, 60_000);
  void enviarPendentes();
  return () => {
    window.removeEventListener('online', aoVoltar);
    window.removeEventListener('offline', aoCair);
    document.removeEventListener('visibilitychange', aoMostrar);
    clearInterval(periodico);
    if (reagendar) clearTimeout(reagendar);
  };
}
